import { CaretRightOutlined, DeleteOutlined, DownOutlined, EyeOutlined, PauseOutlined, RightOutlined, StopOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { useEffect, useState } from 'react';
import { useTaskExecListener } from '../../apis/agent/TaskExecListener';
import { deleteTaskExec, getChildrenTaskExec, pauseTaskExec, resumeTaskExec, stopTaskExec, TaskExec } from '../../apis/mgmt/api';
import { DataViewProps, readOnly } from '../common/DataView';
import { useStatefulList } from '../common/List';
import { TaskProgressBar } from './TaskProgressBar';


export function TaskExecTree(props: 
    DataViewProps<TaskExec> & {
        level?: number,
        onShowLog?: (taskExec: TaskExec) => void,
        onShowFileLog?: (taskExec: TaskExec) => void
    }
) {
    const initialData = props.data;
    const id = initialData?.id;

    const { data } = useTaskExecListener(props)

    const [collapsed, setCollapsed] = useState<boolean>(true);
    const nChildren = data?.childrenCount || 0;
    const childrenList = useStatefulList<TaskExec>();
    useEffect(() => {
        if (!id) return;
        if (nChildren==0 || collapsed) childrenList.setData([]);
        else getChildrenTaskExec(id).then((response) => childrenList.setData(response.data) );
    }, [nChildren, collapsed]);

    const level_padding = 14; // padding for each level
    const baseTextWidth = 200; // Formula to calculate the width of the text

    const level = props.level || 0;
    const textWidth = baseTextWidth - (level * level_padding);

    const s = data?.logSummary;

    const indeterminate_saved = (s?.createdOrUpdated||0) - (s?.created||0) - (s?.updated||0);

    //console.log(`TaskExecTree ${data?.id} ${data?.name} ${data?.progress}`);

    var clickCount = 0;
    const onEyeClick = (data: TaskExec) => {
        clickCount++;
        setTimeout(() => {
            if (clickCount==1) { // single
                props.onShowLog && props.onShowLog(data);
            } else if (clickCount>1) { // double
                props.onShowFileLog && props.onShowFileLog(data);
            }
            clickCount = 0;
        }, 250)
    }

    const [ pausing, setPausing ] = useState<boolean>(false);
    const onPauseClick = (data: TaskExec) => { 
        if (!data?.id) return;
        setPausing(true);
        pauseTaskExec(data.id); 
    }
    useEffect(() => {
        if (data?.paused || data?.stopped) setPausing(false);
    }, [data?.paused, data?.stopped, pausing]);

    const [ resuming, setResuming ] = useState<boolean>(false);
    const onResumeClick = (data: TaskExec) => { 
        if (!data?.id) return;
        setResuming(true);
        resumeTaskExec(data.id); 
    }
    useEffect(() => {
        if (!data?.paused) setResuming(false);
    }, [data?.paused, resuming]);

    const [ stopping, setStopping ] = useState<boolean>(false);
    const onStopClick = (data: TaskExec) => { 
        if (!data?.id) return;
        setStopping(true);
        stopTaskExec(data.id); 
    }
    useEffect(() => {
        if (data?.stopped) setStopping(false);
    }, [data?.stopped]);

    const onDeleteClick = (data: TaskExec) => { 
        data?.id && deleteTaskExec(data.id).then(() => {
            props.setData(undefined); // clear the data after deletion
        }); 
    }

    return data && (
        <div className='parent'>
            <Row className='rowContent'>
                <Col>
                    <Row>
                        <Col>
                            <div className="cont1_">
                                <div className="iconCont">
                                    {nChildren && <a onClick={() => setCollapsed(!collapsed) }> 
                                        {collapsed ? <RightOutlined /> : <DownOutlined />}
                                    </a> || <a> </a>}
                                </div>
                                <div className="textCont" style={{width:textWidth}}>
                                    {data.name} #{data.id?.split('@')[0]}
                                </div><div className="perCont">
                                    <TaskProgressBar height={10} 
                                        created={s?.created}
                                        updated={(s?.updated||0) + (s?.enabled||0) + (s?.disabled||0)}
                                        deleted={s?.deleted}
                                        saved={indeterminate_saved}
                                        oksWithWarnings={s?.oksWithWarnings}
                                        errors={s?.errors}
                                        percent={data.progress}
                                        fatal={s?.fatal}
                                    />
                                </div>
                                <div className="iconCont">
                                    {!nChildren && (props.onShowLog || props.onShowFileLog) && <a 
                                        onClick={() => onEyeClick(data)}
                                    >
                                        <EyeOutlined />
                                    </a>}
                                </div>
                                {!nChildren && !data.finished && !data.stopped && <div className="iconCont">
                                    {data.paused && <a onClick={() => onResumeClick(data)}>
                                        <CaretRightOutlined className={resuming ? 'blink' : ''} />
                                    </a> || <a onClick={() => onPauseClick(data)}>
                                        <PauseOutlined className={pausing ? 'blink' : ''} />
                                    </a>}
                                </div>}
                                {!nChildren && !data.finished && <div className="iconCont">
                                    {!data.stopped && <a onClick={() => onStopClick(data)}>
                                        <StopOutlined className={stopping ? 'blink' : ''} />
                                    </a> ||  
                                        <StopOutlined disabled />
                                    }
                                </div>}
                                { (!data.rootId || data.rootId==data.id) && (data.finished || !data.started) && <div className="iconCont">
                                    <a onClick={() => onDeleteClick(data)}>
                                        <DeleteOutlined />
                                    </a>
                                </div>}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col><div className='legend'>
                            {s?.created && `${s.created} created ` || ''} 
                            {s?.updated && `${s.updated} updated ` || ''}
                            {s?.deleted && `${s.deleted} deleted ` || ''}
                            {s?.enabled && `${s.enabled} enabled ` || ''}
                            {s?.disabled && `${s.disabled} disabled ` || ''}
                            {indeterminate_saved && `${indeterminate_saved} saved` || ''}
                            {s?.warnings && `${s.warnings} warnings ` || ''}
                            {s?.errors && `${s.errors} errors ` || ''}
                            {!s?.created && !s?.updated && !s?.deleted && !s?.enabled && !s?.disabled && !s?.errors && !s?.warnings && data?.progress==100 && 'Nothing done' || ''}
                        </div></Col>
                    </Row>
                    { s?.fatal && <Row>
                        <Col><div className='legend' style={{color: 'red'}}>    
                            {s.fatal}
                        </div></Col>
                    </Row>}
                </Col>
            </Row>
            {!collapsed &&
                <div className="children">
                    {childrenList.data?.map((child) => {
                        return <TaskExecTree key={child.id} level={level + 1} data={child} {...readOnly} onShowLog={props.onShowLog}/>
                    })}
                </div>
            }
        </div>


    ) || null;

}
